import { useCollectionSidebarItems } from '_/components/Collections/CollectionSidebar';
import { CollectionProvider, useCollection } from '_/contexts/CollectionContext';
import { useSidebar, CONTEXT_DEPTH } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import React, { ReactNode, useEffect } from 'react';

interface CollectionSectionProps {
  children: ReactNode;
}

/**
 * Sidebar manager that consumes CollectionContext
 * Separated from provider to avoid circular dependency
 */
function CollectionSidebarManager({ children }: CollectionSectionProps) {
  const { selectedOrgSlug } = useOrganization();
  const { selectedProjectSlug } = useProject();
  const { selectedCollectionSlug } = useCollection();
  const { setSidebarItems, registerContext, unregisterContext } = useSidebar();
  const sidebarItems = useCollectionSidebarItems(selectedOrgSlug || '', selectedProjectSlug || '', selectedCollectionSlug || '');

  useEffect(() => {
    registerContext(CONTEXT_DEPTH.COLLECTION);
    return () => {
      unregisterContext(CONTEXT_DEPTH.COLLECTION);
    };
  }, [registerContext, unregisterContext]);

  useEffect(() => {
    if (
      selectedCollectionSlug &&
      selectedCollectionSlug !== '_new' &&
      selectedOrgSlug &&
      selectedProjectSlug &&
      sidebarItems.length > 0
    ) {
      setSidebarItems(sidebarItems, CONTEXT_DEPTH.COLLECTION);
    }
  }, [setSidebarItems, sidebarItems, selectedCollectionSlug, selectedOrgSlug, selectedProjectSlug]);

  return <>{children}</>;
}

/**
 * CollectionSection - Wrapper for collection-level pages
 * Combines provider and sidebar manager
 */
function CollectionSection({ children }: CollectionSectionProps) {
  return (
    <CollectionProvider>
      <CollectionSidebarManager>{children}</CollectionSidebarManager>
    </CollectionProvider>
  );
}

export default CollectionSection;
